import { effect, inject, Injectable, signal, untracked } from '@angular/core';
import { getColumnMaxHeight } from '@common/utils';
import { WINDOW } from '@common/window.provider';

@Injectable()
export class ElementMaxHeightService {
  private readonly window = inject(WINDOW);

  private readonly columnHeight = signal(getColumnMaxHeight());
  private readonly refs = signal<Partial<Record<string, HTMLElement>>>({});
  private readonly manualHeights = signal<Partial<Record<string, number>>>({});
  private readonly padding = signal(0);

  public readonly height = this.columnHeight.asReadonly();

  private readonly refsEffect = effect((onCleanup) => {
    const refs = this.refs();

    const observers = Object.entries(refs)
      .map(([, el]) => el)
      .filter(Boolean)
      .map((el) => {
        const observer = new ResizeObserver(() => {
          this.updateHeight(this.refs());
        });

        observer.observe(el);

        return observer;
      });

    onCleanup(() => {
      observers.forEach((observer) => observer.disconnect());
    });
  });

  private readonly heightEffect = effect(() => {
    const refs = this.refs();
    const manualHeights = this.manualHeights();
    const padding = this.padding();

    untracked(() => {
      this.updateHeight(refs, manualHeights, padding);
    });
  });

  updateConfig(overrides?: { customPadding?: number; manualHeights?: Partial<Record<string, number>> }): void {
    this.manualHeights.set(overrides?.manualHeights ?? {});
    this.padding.set(overrides?.customPadding ?? 0);
  }

  updateRef(key: string, el: HTMLElement | null): void {
    this.refs.update((refs) => ({
      ...refs,
      [key]: el ?? undefined
    }));
  }

  private updateHeight(
    refs: Partial<Record<string, HTMLElement>>,
    manualHeights: Partial<Record<string, number>> = this.manualHeights(),
    padding: number = this.padding()
  ): void {
    const measuredHeights: Partial<Record<string, number>> = Object.entries(refs).reduce(
      (acc, [key, el]) => {
        if (!el) return acc;

        acc[this.refToHeightKey(key)] = this.getFullHeight(el);

        return acc;
      },
      {} as Partial<Record<string, number>>
    );

    const height = getColumnMaxHeight({
      ...measuredHeights,
      ...manualHeights,
      customPadding: padding
    });

    this.columnHeight.set(height);
  }

  private refToHeightKey(refKey: string): string {
    return refKey.replace(/Ref$/, 'Height');
  }

  private getFullHeight(el: HTMLElement): number {
    const rect = el.getBoundingClientRect();
    const style = this.window.getComputedStyle(el);

    const marginTop = parseFloat(style.marginTop) || 0;
    const marginBottom = parseFloat(style.marginBottom) || 0;

    return Math.ceil(rect.height + marginTop + marginBottom);
  }
}
